import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Tab, Tabs, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import FolderSpecialOutlinedIcon from "@mui/icons-material/FolderSpecialOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import palette from "../../../common/styles/palette";
import SheAiChecklistService from "../../../services/sheai/SheAiChecklistService";
import useChecklistAiResult from "../../hooks/useChecklistAiResult";
import { eixosDoRelatorio } from "./checklistReport";
import ChecklistReportSection from "./ChecklistReportSection";
import ChecklistVisualSection from "./ChecklistVisualSection";
import ChecklistIntegroSection, {
  ChecklistIntegro,
} from "./ChecklistIntegroSection";
import ChecklistSoftSkillsSection from "./ChecklistSoftSkillsSection";
import ChecklistDossieSection from "./ChecklistDossieSection";
import ChecklistMedicalReviewSection from "./ChecklistMedicalReviewSection";
import AdkTranscriptSection from "./AdkTranscriptSection";
import { AiCallout, AiCard, AiDownloadButton, AiFichaLinha, AiSectionHeader } from "./aiSurface";
import DebriefingVideo from "../debriefing-video/DebriefingVideo";
import { sourceVideoURL } from "../../utils/videoSources";

/**
 * A aba Dados IA: TUDO sobre a análise desta Gravação, em três leituras.
 *
 * "Relatório de análise" é a leitura do professor — consolidação por eixo, com
 * acertos, pontos a melhorar e a pontuação da Habilidade. "Transcrição de
 * áudio" é a fala captada. "Checklist" é o instrumento NA ÍNTEGRA —
 * enunciado, peso, crítico e a Detecção de cada Item, o texto exato que o
 * agente usa para julgar o vídeo. Abaixo do relatório fica a "Análise visual
 * complementar": o resumo do que a IMAGEM sustenta por item — a leitura fina
 * que substituiu o antigo log "item a item" (critério a critério), alinhada
 * à referência do produto.
 *
 * Único ponto que consulta `GET /sheai/checklist/result/{id}` nesta aba: as
 * seções recebem a mesma análise por prop, para não haver duas leituras do
 * bucket na mesma tela nem duas versões na mesma página.
 */
/** gs://bucket/caminho -> URL HTTPS do objeto (bucket de vídeo é público). */
const gsToHttps = (uri: string) =>
  `https://storage.googleapis.com/${uri.replace("gs://", "")}`;

interface ChecklistAiPanelProps {
  debriefingId: number | string;
  styles: { box?: string };
  /** Enunciado por `item_id`, quando a tela já carregou o Checklist. */
  perguntaPorItem?: Record<string, string>;
  /**
   * O Checklist da estação na íntegra (skills + itens + detection), como já
   * vem de `GET /checklist/{id}/debriefing/{id}`. Alimenta a sub-aba
   * "Checklist" — o professor precisa ver o que a IA recebeu, não só o que
   * ela devolveu.
   */
  checklist?: ChecklistIntegro | null;
  /**
   * URL da gravação, já resolvida pelo contexto de vídeo da aba. Alimenta o
   * preview expansível dos timestamps na "Análise visual complementar".
   */
  videoUrl?: string | null;
  /** Resultado STT estruturado quando já disponível */
  sttResult?: {
    speech_possession?: string;
    wordcloud?: string;
    segments_trancriptions?: Array<{
      speaker: string;
      start: number;
      end: number;
      transcription: string;
    }>;
  } | null;
}

const ChecklistAiPanel: React.FC<ChecklistAiPanelProps> = ({
  debriefingId,
  styles,
  perguntaPorItem,
  checklist,
  videoUrl,
  sttResult,
}) => {
  const { t } = useTranslation();
  const { status, msg, analise, revisao, erro, aguardarNovaAnalise } =
    useChecklistAiResult(debriefingId);
  const [aba, setAba] = useState(0);
  const [reprocessando, setReprocessando] = useState(false);
  const [playerMinimized, setPlayerMinimized] = useState(false);
  useEffect(() => { setPlayerMinimized(false); }, [debriefingId]);
  const expandPlayer = useCallback(() => { setPlayerMinimized(false); }, []);

  /**
   * Republica o vídeo no pipeline de IA (tópico `video-ready`). O agente
   * grava um JSON NOVO no bucket — nunca sobrescreve — e o hook detecta a
   * rodada nova pelo `analysis_ts`. Cada chamada gasta UMA análise Gemini;
   * por isso a confirmação antes.
   */
  const reprocessar = async () => {
    const confirmado = window.confirm(
      "Reprocessar dispara uma nova análise de IA sobre o vídeo desta estação (custa processamento do modelo). A análise atual continua disponível até a nova terminar. Continuar?"
    );
    if (!confirmado) return;

    setReprocessando(true);
    try {
      const { data } = await SheAiChecklistService.postReanalyze(debriefingId);
      aguardarNovaAnalise();
      setAba(0);
      toast(data?.msg || "Análise solicitada.", { type: "info" });
    } catch (e: unknown) {
      const resposta = e as { response?: { data?: { msg?: string } } };
      const motivo =
        resposta?.response?.data?.msg || "Não foi possível solicitar a reanálise.";
      toast(motivo, { type: "error" });
    } finally {
      setReprocessando(false);
    }
  };

  const eixos = eixosDoRelatorio(analise, perguntaPorItem);
  const relatorioDocx = (analise?.dna_bundle || []).find((uri) =>
    uri.endsWith(".docx") && uri.includes("relatorio")
  );

  // Fallback: se o checklist não veio por prop (ex.: rota direta sem ?checklistId=),
  // monta a representação a partir dos dados do checklist/scoring embutidos na análise.
  const anyAnalise = analise as any;
  const checklistEfetivo: ChecklistIntegro | null =
    checklist ||
    (anyAnalise
      ? {
          name:
            anyAnalise.checklist?.name ||
            anyAnalise.name ||
            `Checklist #${anyAnalise.checklist_id || anyAnalise.checklist?.checklist_id || ""}`,
          skills: (
            anyAnalise.checklist?.skills ||
            anyAnalise.scoring?.skills ||
            []
          ).map((sk: any) => ({
            name: sk.name,
            points: sk.points,
            passing_score: sk.passing_score,
            items: (anyAnalise.checklist?.items || anyAnalise.items || [])
              .filter((it: any) => !it.skill || it.skill === sk.name)
              .map((it: any) => ({
                item_id: it.item_id,
                question:
                  it.question ||
                  perguntaPorItem?.[String(it.item_id)] ||
                  (it.criteria && it.criteria.length > 0
                    ? it.criteria.map((c: any) => c.criterion).join("; ")
                    : `Item ${it.item_id}`),
                item_value: it.value ?? it.item_value,
                critical: it.critical,
                detection: it.detection || null,
              })),
          })),
        }
      : null);

  // A mídia exibida precisa ser a MESMA que o agent analisou. O lookup legado
  // de gravações pode devolver a primeira câmera/rodada do debriefing.
  const primarySource = analise?.video_sources?.find((source) => source.role === "primary") || analise?.video_sources?.[0];
  const analysisVideoUrl = primarySource
    ? sourceVideoURL(primarySource.uri, primarySource.generation)
    : sourceVideoURL(analise?.video_uri);
  const effectiveVideoUrl = analysisVideoUrl || videoUrl;

  const semAnalise = erro || (status && status !== "finished") || !analise;
  const reciboTeste = analise?.test_receipt;
  const reciboValido = reciboTeste && Array.isArray(reciboTeste.video_sources) &&
    typeof reciboTeste.n_items === "number" && typeof reciboTeste.instructions_received === "boolean";
  const valorDoRecibo = (valor: string | number | boolean) =>
    typeof valor === "boolean" ? t(valor ? "ai_test_receipt_yes" : "ai_test_receipt_no") : valor;
  const detalhesRecibo: [string, string | number | boolean | undefined][] = [
    ["instructions_chars", reciboTeste?.instructions_chars],
    ["scoring_version", reciboTeste?.scoring_version],
    ["item_ids", reciboTeste?.item_ids?.join(", ")],
    ["items_with_detection", reciboTeste?.items_with_detection],
    ["camera2_rules_received", reciboTeste?.camera2_rules_received],
    ["camera2_guidance_received", reciboTeste?.camera2_guidance_received],
    ["video_sources_sha", reciboTeste?.video_sources_sha],
    ["analysis_ts", analise?.analysis_ts],
    ["received_at", analise?.received_at],
    ["model", analise?.call?.model ?? analise?.model],
    ["call_duration_s", analise?.call?.duration_s],
    ["call_engine", analise?.call?.engine],
    ["call_graph", analise?.call?.graph],
    ["call_attempts", analise?.call?.attempts],
  ];

  return (
    <Box className={styles.box} sx={{ mt: 4 }}>
      {/* Cabeçalho da aba: o que é isto e onde baixar a versão offline. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          px: 2.5,
          py: 2,
          mb: 2.5,
          borderRadius: `${palette.radius}px`,
          backgroundColor: palette.brandWash,
          border: `1px solid ${palette.lilacMid}55`,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <AutoAwesomeIcon sx={{ color: palette.brand, mt: 0.25 }} />
          <Box>
            <Typography sx={{ fontWeight: 700, color: palette.brandDark }}>
              {semAnalise ? "Análise SheAI" : "Análise completa"}
            </Typography>
            <Typography variant="body2" sx={{ color: palette.textMuted }}>
              Artefatos gerados a partir do vídeo, do áudio e do checklist da
              estação. Visualize abaixo ou baixe para consulta offline.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Button
            onClick={reprocessar}
            disabled={reprocessando || !!semAnalise || status === "in_progress"}
            startIcon={<RefreshIcon />}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: palette.brand,
              borderColor: palette.brand,
              "&:hover": { borderColor: palette.brandDark },
            }}
          >
            {reprocessando ? "Enviando…" : "Reprocessar análise"}
          </Button>
          {relatorioDocx && (
            <AiDownloadButton href={gsToHttps(relatorioDocx)}>
              Relatório .docx
            </AiDownloadButton>
          )}
        </Box>
      </Box>

      {!semAnalise && reciboValido && reciboTeste && (
        <AiCard>
          <AiSectionHeader
            titulo={t("ai_test_receipt_title")}
            subtitulo={t("ai_test_receipt_help")}
          />
          <AiFichaLinha rotulo={t("ai_test_receipt_checklist")}>
            {reciboTeste.checklist_id ?? t("ai_test_receipt_missing")}
          </AiFichaLinha>
          <AiFichaLinha rotulo={t("ai_test_receipt_items")}>
            {reciboTeste.n_items}
          </AiFichaLinha>
          <AiFichaLinha rotulo={t("ai_test_receipt_sources")}>
            {reciboTeste.video_sources.length === 0
              ? t("ai_test_receipt_no_sources")
              : reciboTeste.video_sources.map((source) => (
                  <Box key={source.source_id} sx={{ mb: 1.5 }}>
                    <Box sx={{ fontWeight: 600 }}>
                      {source.source_id} · {t(`ai_test_receipt_role_${source.role}`, { defaultValue: source.role })}
                      {source.generation && ` · ${t("ai_test_receipt_generation")} ${source.generation}`}
                    </Box>
                    {source.uri && <Box>{t("ai_test_receipt_filename")}: {source.uri.split("/").pop()?.split("?")[0]}</Box>}
                    <Box>
                      {t("ai_test_receipt_duration_s")}: {source.duration_s ?? t("ai_test_receipt_missing")}
                    </Box>
                    <Box>
                      {t("ai_test_receipt_offset_ms")}: {source.offset_ms ?? t("ai_test_receipt_offset_unknown")}
                    </Box>
                    {Object.entries(source.media ?? {}).filter(([, valor]) => valor != null).map(([campo, valor]) => (
                      <Box key={campo}>
                        {t(`ai_test_receipt_media_${campo}`, { defaultValue: campo })}: {valorDoRecibo(valor)}
                      </Box>
                    ))}
                  </Box>
                ))}
          </AiFichaLinha>
          <AiFichaLinha rotulo={t("ai_test_receipt_instructions")}>
            {t(reciboTeste.instructions_received ? "ai_test_receipt_received" : "ai_test_receipt_not_received")}
          </AiFichaLinha>
          {reciboTeste.checklist_spec_sha && (
            <AiFichaLinha rotulo={t("ai_test_receipt_hash")}>
              {reciboTeste.checklist_spec_sha}
            </AiFichaLinha>
          )}
          {detalhesRecibo.filter(([, valor]) => valor !== undefined).map(([campo, valor]) => (
            <AiFichaLinha key={campo} rotulo={t(`ai_test_receipt_${campo}`)}>
              {valorDoRecibo(valor as string | number | boolean)}
            </AiFichaLinha>
          ))}
        </AiCard>
      )}

      <Tabs
        value={aba}
        onChange={(_, valor) => setAba(valor)}
        sx={{ borderBottom: `1px solid ${palette.border}`, mb: 3 }}
      >
        <Tab
          icon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Relatório de análise"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
        />
        <Tab
          icon={<PsychologyOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Soft Skills"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
        />
        <Tab
          icon={<MicNoneOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Transcrição de áudio"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
        />
        <Tab
          icon={<ChecklistOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Checklist"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
        />
        <Tab
          icon={<FolderSpecialOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Dossiê (Calibração SheAI)"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
        />
        <Tab
          icon={<LocalHospitalOutlinedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Parecer médico"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
        />
      </Tabs>

      {!semAnalise && effectiveVideoUrl ? (
        <AiCard>
          <DebriefingVideo
            video={effectiveVideoUrl}
            analysisSources={analise.video_sources}
            presentation="analysis"
            transcript={analise.transcript}
            minimized={playerMinimized}
            minimizedContentId="dados-ia-video"
            onToggleMinimized={() => setPlayerMinimized(!playerMinimized)}
            onEvidenceSeek={expandPlayer}
            notMarker
            heightVideo="360px"
          />
        </AiCard>
      ) : null}

      {aba === 0 &&
        (semAnalise ? (
          <Typography variant="body2" sx={{ color: palette.textMuted }}>
            {erro
              ? "Não foi possível consultar a análise da IA."
              : status === "not_found"
              ? "Nenhuma análise de vídeo registrada para esta gravação. Ela é disparada automaticamente assim que a gravação da estação fica disponível."
              : status === "failed"
              ? msg || "A análise falhou no agente."
              : msg || "Análise em processamento."}
          </Typography>
        ) : (
          <>
            {/* Identidade do aluno. Só aparece quando a divergência é
                AFIRMATIVA (`verified === false`: houve rosto reconhecível e
                ele não casou com o registro). Ausente ou `null` não vira
                aviso — não houve como opinar, e alarmar sem prova treinaria
                o professor a ignorar o aviso justamente quando ele importa.
                Não bloqueia nada, por decisão do cliente: quem decide é o
                professor. */}
            {analise?.identity?.verified === false && (
              <AiCallout
                tom="atencao"
                titulo="A pessoa no vídeo pode não ser o aluno desta conta"
              >
                A comparação com o rosto registrado pelo aluno não bateu
                {typeof analise.identity?.score === "number" &&
                  ` (semelhança ${analise.identity.score.toFixed(3)}, mínimo ${analise.identity.threshold})`}
                . A avaliação foi feita normalmente — confira o vídeo antes de
                lançar a nota.
              </AiCallout>
            )}
            <ChecklistReportSection
              eixos={eixos}
              analise={analise}
              checklist={checklistEfetivo}
              relatorioDocx={relatorioDocx ? gsToHttps(relatorioDocx) : undefined}
            />
            <ChecklistVisualSection
              visual={analise?.visual_analysis}
              analise={analise}
              perguntaPorItem={perguntaPorItem}
              videoUrl={effectiveVideoUrl}
            />
          </>
        ))}
      {aba === 1 && (
        <ChecklistSoftSkillsSection
          analise={analise}
          sttResult={sttResult}
        />
      )}

      {/* A transcrição é a MESMA leitura do agente ADK (audio_asr_agent),
          não o STT legado — uma fonte só para a fala captada, com timestamps
          clicáveis. */}
      {aba === 2 && <AdkTranscriptSection analise={analise} />}

      {/* O instrumento que a IA RECEBEU: enunciado, peso, crítico e Detecção
          de cada Item. Não depende da análise. */}
      {aba === 3 && <ChecklistIntegroSection checklist={checklistEfetivo} />}

      {/* Dossiê de Avaliação, De-Para de divergências e reprocessamento com guidance */}
      {aba === 4 && (
        <ChecklistDossieSection
          debriefingId={debriefingId as any}
          analise={analise}
          revisao={revisao}
          checklist={checklistEfetivo}
          sttResult={sttResult}
          onReanalyzeSuccess={reprocessar}
        />
      )}

      {/* Segunda leitura, médica e sem o vídeo, sobre a análise vigente:
          sustentação clínica dos vereditos, o que a estação não cobrou e o que
          ensinar no debriefing. Opinião, nunca nota. */}
      {aba === 5 && (
        <ChecklistMedicalReviewSection
          debriefingId={debriefingId}
          analise={semAnalise ? null : analise}
          perguntaPorItem={perguntaPorItem}
        />
      )}
    </Box>
  );
};
export default ChecklistAiPanel;
